import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { captureFrontendException, setSentryUser } from '../utils/sentryMonitoring';

/**
 * AuthContext — single source of truth for Firebase auth state and role flags.
 *
 * Eliminates the duplicate onAuthStateChanged listeners that previously existed
 * in both App.js and Header.js (causing 2× Firestore reads per login and
 * potential inconsistencies between the two role checks).
 *
 * Both App.js and Header.js now call useAuth() to read from this single context.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isRegionLead, setIsRegionLead] = useState(false);
  const [isMason, setIsMason] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const [adminDoc, workerDoc] = await Promise.all([
            getDoc(doc(db, 'admins', currentUser.uid)),
            // worker_auth is the canonical worker collection used throughout the app
            getDoc(doc(db, 'worker_auth', currentUser.uid)),
          ]);

          const isAdminUser = adminDoc.exists();
          const role = adminDoc.data()?.role;

          let isWorkerUser = false;
          if (workerDoc.exists()) {
            const workerData = workerDoc.data();
            if (workerData.approvalStatus === 'approved') {
              isWorkerUser = true;
            }
          }

          setIsAdmin(isAdminUser);
          setIsWorker(isWorkerUser);
          setAdminRole(role);
          setIsSuperAdmin(isAdminUser && role === 'superadmin');
          setIsRegionLead(isAdminUser && (role === 'regionLead' || role === 'region-lead'));
          setIsMason(isAdminUser && role === 'mason');
          setSentryUser(currentUser, role || (isWorkerUser ? 'worker' : 'consumer'));
        } catch (error) {
          captureFrontendException(error, { source: 'auth_role_lookup' });
          setSentryUser(currentUser, 'unknown');
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsRegionLead(false);
          setIsMason(false);
          setIsWorker(false);
          setAdminRole(null);
        }
      } else {
        setSentryUser(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsRegionLead(false);
        setIsMason(false);
        setIsWorker(false);
        setAdminRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isSuperAdmin,
        isRegionLead,
        isMason,
        isWorker,
        adminRole,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to consume the shared auth state.
 * Use in any component instead of setting up a new onAuthStateChanged listener.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
